import React, { useState, useContext, useEffect, useMemo, useCallback } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";

import { ChildrenContext, ChildrenProvider } from "../../context/children";
import { MeProvider } from "../../context/me";
import { useMeContext } from "@/util/me-context";
import { APIContext, APIProvider } from "../../context/api";

import Navigation from "../../components/Navigation";
import Header from "../../components/Header";
import NoChild from "../../components/NoChild";
import MobileFooter from "../../components/MobileFooter";
import { isIosApp, trackPage } from "../../util/helper";
import ModalBirthDate from "../../components/ModalBirthDate";
import moment from "moment";
import { ChildResponse, PaymentStatusResponse } from "../../openapi";
import { Box } from "@mui/material";
import Grid from "@mui/material/Grid2";
import { sendUserAttributeVerificationCode } from "aws-amplify/auth";
import { LinkBilling } from "../../routes/account/LinkBilling";
import { PaymentStatus } from "../../openapi/models/payment-status";
import { UnpaidSubscription } from "../../routes/account/UnpaidSubscription";
import { PausedSubscription } from "../../routes/account/PausedSubscription";
import { MissingPaymentMethod } from "../../routes/account/MissingPaymentMethod";
import { handleApiError } from "../../util/error-handlers";
import { useIsMobile } from "@/hooks";
import { NotificationProvider, useNotificationContext } from "@/components/NotificationCenter/notification-context";
import NoSubscriptionModal from "@/components/NoSubscriptionModal";
import Loader from "@/components/CircularLoader";
import { ChildProvider, useChildContext } from "@/context/child";

const newLayoutRoutes = ["/phone-history", "/device-auth"];

const checkIfNewLayoutRoute = (pathname: string): boolean => {
  return newLayoutRoutes.some((route) => pathname.includes(route));
}

const DashboardPage: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { getCaregiverService } = useContext(APIContext);
  const { data: childrenUsers } = useContext(ChildrenContext);
  const { data: user, logout, caregiverData, refreshCaregiverData } = useMeContext();
  const { child, setChild } = useChildContext();
  const [noBirthDayChilds, setNoBirthDayChilds] = useState<ChildResponse[]>([]);
  const [showConnectBillingBanner, setShowConnectBillingBanner] = useState(false);
  const [windowInnerHeight, setWindowInnerHeight] = useState(window.innerHeight);
  const [paymentStatusResponse, setPaymentStatusResponse] = useState<PaymentStatusResponse | undefined>(undefined);
  const [isRequestingPaymentStatus, setIsRequestingPaymentStatus] = useState(true);
  const location = useLocation();
  const navigate = useNavigate();
  const isMobile = useIsMobile();
  const { markAsRead } = useNotificationContext();
  const sharedChildrenUsers = useMemo(() => {
    return childrenUsers?.filter((child) => child.caregiverId !== user?.userId) || [];
  }, [childrenUsers, user]);

  const handleResizeAndOrientation = () => {
    setWindowInnerHeight(window.innerHeight);
  };

  const isPinwheelUser = useMemo(() => {
    return user?.attributes.email?.endsWith("@pinwheel.com");
  }, [user]);

  const showLinkBilling = useMemo(() => {
    // If the user has only shared children, then don't show the link billing email component.
    if (sharedChildrenUsers.length > 0 && sharedChildrenUsers.length === childrenUsers?.length) return false;
    // If billing email is not available, then show the link billing email component. 
    // If the user is a Pinwheel user, then don't show the link billing email component.
    if ((!paymentStatusResponse || showConnectBillingBanner) && !isPinwheelUser) return true;
    return false;
  }, [childrenUsers?.length, isPinwheelUser, paymentStatusResponse, sharedChildrenUsers.length, showConnectBillingBanner]);

  useEffect(() => {
    window.addEventListener("resize", handleResizeAndOrientation);
    // Listening to orientation change as iOS RN WebView is not firing resize events between landscape to portrait.
    window.addEventListener("orientationchange", handleResizeAndOrientation);
    return () => {
      window.removeEventListener("resize", handleResizeAndOrientation);
      window.removeEventListener("orientationchange", handleResizeAndOrientation);
    };
  }, []);

  useEffect(() => {
    trackPage(location.pathname);
  }, [location]);

  const refreshPaymentStatus = useCallback(async (nextPaymentCheckAt: number) => {
    try {
      const caregiverService = await getCaregiverService();
      const paymentStatusCheckResult = (await caregiverService.getSubscriptionPaymentStatus()).data;
      setPaymentStatusResponse(paymentStatusCheckResult);
      localStorage.setItem("pcat", String(nextPaymentCheckAt));
    } catch(error) {
      handleApiError(error);
    } finally {
      setIsRequestingPaymentStatus(false);
    }
  }, [getCaregiverService]);

  useEffect(() => {
    if (child?.deviceInfos && location.pathname === "/") {
      if (child.deviceInfos.length > 0) {
        navigate("/phone-history", { replace: true });
      } else {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [child, location.pathname, navigate])

  useEffect(() => {
    // If the user has only shared children, then don't make the payment status check api call.
    if (sharedChildrenUsers.length > 0 && sharedChildrenUsers.length === childrenUsers?.length) {
      setIsRequestingPaymentStatus(false);
      return;
    };

    // Make payment status check api call:
    // 1. after login
    // 2. page reload
    // 3. once in every day
    // LocalStorage will keep next timestamp for making api call. (Simpler approach for first release)
    // A timer will checking if current time has elapsed the next payment check timestamp before making api call.

    const oneHourInMs = 60 * 60 * 1000; // 1h in milliseconds
    // Run timer in every hour so that within 1 hour of next payment check timestamp we make the call.
    const checkPaymentTimerInterval = oneHourInMs;
    const dayInMs = 24 * oneHourInMs; // 24h in milliseconds

    const checkPayment = async () => {
      const now = new Date().getTime();
      const paymentCheckAt = Number(localStorage.getItem("pcat")) || 0;
      const nextPaymentCheckAt = now + dayInMs;

      if (paymentStatusResponse === undefined || paymentCheckAt === 0 || typeof paymentCheckAt !== "number") {
        // Uninitialized or malformed pcat value.
        await refreshPaymentStatus(nextPaymentCheckAt);
      } else if (now > paymentCheckAt) {
        // Time to check payment status again.
        await refreshPaymentStatus(nextPaymentCheckAt);
      }
    };

    // Run payment check once at component mount (after login, page reload)
    checkPayment();

    // Then run payment check after every given interval.
    const checkPaymentTimer = setInterval(checkPayment, checkPaymentTimerInterval);

    return () => {
      clearInterval(checkPaymentTimer);
    };
  }, [childrenUsers?.length, paymentStatusResponse, refreshPaymentStatus, sharedChildrenUsers.length]);
  const [showBirthDateModal, setShowBirthDateModal] = useState(false);

  useEffect(() => {
    const urlSearchParams = new URLSearchParams(location.search);
    const childrenId = urlSearchParams.get("childrenId");
    const notificationId = urlSearchParams.get("notificationId");
    if (notificationId) {
      markAsRead(notificationId);
      urlSearchParams.delete("notificationId");
    }
    if (childrenId) {
      const child = childrenUsers?.find((child) => child.id === childrenId);
      if (child) {
        setChild(child);
        urlSearchParams.delete("childrenId");
        navigate(
          {
            pathname: location.pathname,
            search: urlSearchParams.toString(),
          },
          {
            replace: true,
          },
        );
        return;
      } else {
        urlSearchParams.delete("childrenId");
        navigate(
          {
            pathname: location.pathname,
            search: urlSearchParams.toString(),
          },
          {
            replace: true,
          },
        );
      }
    }
  }, [childrenUsers, location.pathname, location.search, markAsRead, navigate, setChild]);

  useEffect(() => {
    if (caregiverData) {
      if (caregiverData.billingEmail) {
        setShowConnectBillingBanner(false);
      } else {
        setShowConnectBillingBanner(true);
      }
    }
  }, [caregiverData]);

  const currentDate = moment();

  const checkLocalStorage =
    !localStorage.getItem("birthdayCheked") ||
    currentDate.diff(moment(new Date(localStorage.getItem("birthdayCheked")!)), "days") > 6;

  useEffect(() => {
    if (childrenUsers && childrenUsers.length > 0) {
      const childsWithoutBirthday = childrenUsers.filter(child => !child.birthYearMonth);
      if (childsWithoutBirthday.length > 0 && checkLocalStorage) {
        setShowBirthDateModal(true);
        setNoBirthDayChilds(childsWithoutBirthday);
        localStorage.setItem("birthdayCheked", JSON.stringify(currentDate));
      }
    }
  }, [checkLocalStorage, childrenUsers, currentDate]);

  useEffect(() => {
    if (user && user.attributes.email_verified !== true) {
      // send the user a verification code and after its done, redirect
      sendUserAttributeVerificationCode({ userAttributeKey: "email" }).then(() => {
        navigate({
          pathname: "/verify-email",
          search: `redirect=${location.pathname}`,
        });
      });
    }
  }, [location.pathname, navigate, user]);


  if (user?.attributes.email_verified !== true) {
    return null;
  }

  const excludeScrollRoutes = ["/phone-history", "/phone-history/call", "/apps"]

  const handleBillingUpdated = () => {
    refreshCaregiverData?.();
  }

  if (!caregiverData || isRequestingPaymentStatus) return <Loader />;
  if (paymentStatusResponse?.paymentStatus === PaymentStatus.NoBillableSubscription && isIosApp) return <NoSubscriptionModal refreshPaymentStatus={refreshPaymentStatus} />;

  return (
    <>
      <ModalBirthDate
        showModal={showBirthDateModal}
        setShowModal={setShowBirthDateModal}
        childrenList={noBirthDayChilds}
      />
      {showLinkBilling ?
        <LinkBilling
          refreshCaregiverData={handleBillingUpdated}
          refreshPaymentStatus={refreshPaymentStatus}
        />
        :
        (paymentStatusResponse?.paymentStatus === PaymentStatus.PausedSubscription) ?
          <PausedSubscription />
          :
        (paymentStatusResponse?.paymentStatus === PaymentStatus.MissingPaymentMethod) ?
          <MissingPaymentMethod />
          :
        (paymentStatusResponse?.paymentStatus === PaymentStatus.UnpaidSubscription) ?
          <UnpaidSubscription paymentStatusResponse={paymentStatusResponse} setPaymentStatusResponse={setPaymentStatusResponse} />
          :
          <Box sx={{}}>
            <Grid sx={{ display: "flex" }}>
              {!isMobile ?
                <Grid
                  className="sidebar"
                  position="relative"
                  flex={{ lg: "0 0 215px" }}
                >
                  <Navigation />
                </Grid>
                : null}
              <Grid
                flex={1}
                display="flex"
                flexDirection="column"
                sx={{ overflow: "auto", height: `${windowInnerHeight}px` }}
                container
              >
                <Box>
                  <Header
                    onLogoutPress={logout!}
                  />
                </Box>
                <Box
                  sx={{
                    flex: 1,
                    overflowY: {
                      xs: !excludeScrollRoutes.includes(location.pathname) ? "auto" : "hidden",
                      lg: "auto",
                    },
                    overflowX: "hidden",
                    maxWidth: "100%",
                    ...(checkIfNewLayoutRoute(location.pathname) ? { display: "flex", flexDirection: "column" } : {}),
                  }}
                >
                  <Box
                    p={checkIfNewLayoutRoute(location.pathname) ? 0 : 2}
                    pb={0}
                    sx={{
                      ...(checkIfNewLayoutRoute(location.pathname) ? { height: "100%", display: "flex", flex: 1 } : {}),
                    }}
                  >
                    {!child && location.pathname !== "/account" ? <NoChild /> : children}
                  </Box>
                </Box>
                {isMobile ?
                  <Box>
                    <MobileFooter />
                  </Box> : null}
              </Grid>
            </Grid>
          </Box>
      }
    </>
  );
}

const DashboardPageWithWrappers: React.FC = () => (
  <APIProvider>
    <MeProvider>
      <ChildrenProvider>
        <ChildProvider>
          <NotificationProvider>
            <DashboardPage>
              <Outlet />
            </DashboardPage>
          </NotificationProvider>
        </ChildProvider>
      </ChildrenProvider>
    </MeProvider>
  </APIProvider>
);

export default DashboardPageWithWrappers;
